import { lazy } from "react";

const recoverFromImportError = (error: unknown): boolean => {
  const isModuleLoadError =
    error instanceof TypeError &&
    /^(Failed to fetch dynamically imported module:|error loading dynamically imported module:|Importing a module script failed)/.test(
      error.message,
    );
  const isStylesheetLoadError =
    error instanceof Error && error.message.startsWith("Unable to preload CSS for ");

  if ((!isModuleLoadError && !isStylesheetLoadError) || !navigator.onLine) {
    return false;
  }

  // Old tabs can reference chunks removed by a deployment. Persist the guard
  // across page reloads, and never clear it when an unrelated import succeeds.
  const release = process.env.SENTRY_RELEASE || process.env.RELEASE_NAME || "unknown";
  const storageKey = `dashboard:import-reload:${release}`;

  try {
    if (window.sessionStorage.getItem(storageKey)) {
      return false;
    }

    window.sessionStorage.setItem(storageKey, "1");

    // If storage is blocked, leave the normal error boundary available instead
    // of risking an unbounded reload loop.
    if (window.sessionStorage.getItem(storageKey) !== "1") {
      return false;
    }

    window.location.reload();

    return true;
  } catch {
    return false;
  }
};

export const lazyWithRecovery: typeof lazy = load =>
  lazy(async () => {
    try {
      return await load();
    } catch (error) {
      if (recoverFromImportError(error)) {
        // Keep Suspense visible while navigation replaces the stale bundle.
        return new Promise<never>(() => {
          // The new page owns the next import attempt.
        });
      }

      throw error;
    }
  });
